import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { GoogleLogo, ArrowLeft, Sparkle } from "@phosphor-icons/react";

const PROPERTY_TYPES = [
  { v: "hotel", l: "Hotel" },
  { v: "agriturismo_bb", l: "Agriturismi e Bed & Breakfast" },
  { v: "casa_vacanza", l: "Case vacanze" },
  { v: "villa_castello_dimora", l: "Castelli, Ville e Dimore storiche" },
  { v: "chalet", l: "Chalet / Baita" },
  { v: "yacht", l: "Yacht / Barca" },
  { v: "yurta", l: "Yurta / Glamping" },
  { v: "ristorante", l: "Ristorante / Osteria" },
  { v: "spa", l: "SPA / Centro benessere / Terme" },
  { v: "parcheggio", l: "Parcheggi" },
  { v: "altro", l: "Altro" },
];

export default function AuthPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { login, signup, googleLogin } = useAuth();
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", full_name: "", property_name: "", property_type: "hotel",
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (mode === "signup") {
        await signup({ ...form, ref: new URLSearchParams(window.location.search).get("ref") || localStorage.getItem("otelya_ref") || undefined });
        toast.success("Benvenuto in Otelya!");
      } else {
        await login(form.email, form.password);
        toast.success("Bentornato!");
      }
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore, riprova");
    } finally { setBusy(false); }
  };

  const doGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img alt="" src="https://images.unsplash.com/photo-1673403731036-30ef7a4995f5?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/40" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-canvas">
          <Logo light />
          <div>
            <div className="text-xs uppercase tracking-widest opacity-80 flex items-center gap-2"><Sparkle weight="fill" size={12} className="text-gold"/> Otelya</div>
            <h2 className="font-serif text-4xl lg:text-5xl mt-4 leading-[1.05] max-w-md">
              La receptionist <em className="italic text-gold">che non dorme mai</em>.
            </h2>
            <p className="mt-4 max-w-md text-canvas/80">Prenotazioni, ospiti, fornitori, pagamenti — un'unica piattaforma per la tua struttura.</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col bg-canvas">
        <div className="p-6 lg:p-10 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone hover:text-ink" data-testid="back-home"><ArrowLeft size={16}/> Torna al sito</Link>
          <div className="lg:hidden"><Logo size="sm" /></div>
        </div>
        <div className="flex-1 grid place-items-center px-6 lg:px-10 pb-16">
          <div className="w-full max-w-md">
            <div className="text-xs uppercase tracking-widest text-stone">{mode === "signup" ? "Crea il tuo account" : "Accedi al tuo Otelya"}</div>
            <h1 className="font-serif text-3xl lg:text-4xl mt-3 text-ink leading-tight">
              {mode === "signup" ? "Attiva la tua receptionist AI" : "Bentornato"}
            </h1>

            <Button type="button" onClick={doGoogle} disabled={busy} variant="outline"
              className="w-full mt-8 h-11 rounded-full border-line hover:bg-sand" data-testid="google-login-btn">
              <GoogleLogo size={18} weight="bold" className="mr-2 text-ink"/> Continua con Google
            </Button>

            <div className="my-6 flex items-center gap-3 text-xs text-stone">
              <div className="flex-1 h-px bg-line" /> oppure con email <div className="flex-1 h-px bg-line" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              {mode === "signup" && (
                <>
                  <div>
                    <Label>Nome completo</Label>
                    <Input required value={form.full_name} onChange={(e)=>upd("full_name",e.target.value)} placeholder="Es. Chiara Bianchi"
                      className="mt-1.5 h-11 rounded-lg" data-testid="signup-name" />
                  </div>
                  <div>
                    <Label>Nome della struttura</Label>
                    <Input required value={form.property_name} onChange={(e)=>upd("property_name",e.target.value)} placeholder="Es. Agriturismo Il Cipresso"
                      className="mt-1.5 h-11 rounded-lg" data-testid="signup-property-name" />
                  </div>
                  <div>
                    <Label>Tipo di struttura</Label>
                    <Select value={form.property_type} onValueChange={(v)=>upd("property_type", v)}>
                      <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="signup-property-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PROPERTY_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div>
                <Label>Email</Label>
                <Input required type="email" value={form.email} onChange={(e)=>upd("email",e.target.value)} placeholder="tua@email.it"
                  className="mt-1.5 h-11 rounded-lg" data-testid="auth-email" />
              </div>
              <div>
                <Label>Password</Label>
                <Input required type="password" value={form.password} onChange={(e)=>upd("password",e.target.value)} placeholder="Almeno 6 caratteri"
                  className="mt-1.5 h-11 rounded-lg" data-testid="auth-password" />
              </div>

              <Button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-ink text-canvas hover:bg-olive mt-2" data-testid="auth-submit-btn">
                {busy ? "Attendere…" : (mode === "signup" ? "Crea account" : "Accedi")}
              </Button>
            </form>

            <div className="mt-6 text-sm text-stone text-center">
              {mode === "signup" ? "Hai già un account?" : "Non hai un account?"}{" "}
              <button className="text-ink underline underline-offset-4 hover:text-terracotta" onClick={() => setMode(mode==="signup"?"login":"signup")} data-testid="toggle-auth-mode">
                {mode === "signup" ? "Accedi" : "Registrati"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
